import {
  type InfiniteData,
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { api, queryKeys } from '@/shared/api';

import { type NoteAnchor, reorderNotes } from './model';
import type { Note, NoteConverted, NotesPage } from './types';

const PAGE_SIZE = 30;

type Feed = InfiniteData<NotesPage>;

/** The feed, newest-on-top order of the user's own arrangement, loaded page by page (keyset cursor). */
export function useNotesFeed() {
  return useInfiniteQuery({
    queryKey: queryKeys.notes.feed,
    queryFn: ({ pageParam, signal }) =>
      api.get<NotesPage>('/v1/notes', {
        signal,
        query: { limit: PAGE_SIZE, cursor: pageParam ?? undefined },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
  });
}

/** Every loaded note, in feed order. */
export function allNotes(data: InfiniteData<NotesPage> | undefined): Note[] {
  return data?.pages.flatMap((page) => page.items) ?? [];
}

function mapFeed(client: QueryClient, change: (notes: Note[]) => Note[]): void {
  client.setQueryData<Feed>(queryKeys.notes.feed, (current) => {
    if (!current) return current;
    const next = change(current.pages.flatMap((page) => page.items));
    // Keep the page structure (and their cursors); only the items move.
    let offset = 0;
    return {
      ...current,
      pages: current.pages.map((page, index) => {
        const size = index === current.pages.length - 1 ? next.length - offset : page.items.length;
        const items = next.slice(offset, offset + Math.max(size, 0));
        offset += items.length;
        return { ...page, items };
      }),
    };
  });
}

export function useCreateNote() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (markdown_text: string) => api.post<Note>('/v1/notes', { markdown_text }),
    onSuccess: (note) => {
      // A new note goes on top of the feed.
      mapFeed(client, (notes) => [note, ...notes]);
    },
  });
}

export function useUpdateNote() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ note, markdown_text }: { note: Note; markdown_text: string }) =>
      api.patch<Note>(`/v1/notes/${note.id}`, { markdown_text, expected_version: note.version }),
    onSuccess: (updated) => {
      mapFeed(client, (notes) => notes.map((n) => (n.id === updated.id ? updated : n)));
    },
    onError: () => client.invalidateQueries({ queryKey: queryKeys.notes.feed }),
  });
}

export function useDeleteNote() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) => api.delete(`/v1/notes/${noteId}`),
    onSuccess: (_data, noteId) => {
      mapFeed(client, (notes) => notes.filter((n) => n.id !== noteId));
    },
  });
}

/** Reorder: optimistic, rolled back on error; only the moved note's key changes on the server. */
export function useMoveNote() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId, anchor }: { noteId: string; anchor: NoteAnchor; order: string[] }) =>
      api.post<Note>(`/v1/notes/${noteId}/move`, anchor),
    onMutate: async ({ order }) => {
      await client.cancelQueries({ queryKey: queryKeys.notes.feed });
      const previous = client.getQueryData<Feed>(queryKeys.notes.feed);
      mapFeed(client, (notes) => reorderNotes(notes, order));
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) client.setQueryData(queryKeys.notes.feed, context.previous);
    },
    onSettled: () => client.invalidateQueries({ queryKey: queryKeys.notes.feed }),
  });
}

/**
 * Copy a note into a new card at the end of a column. The note stays. `requestId` is created
 * once per gesture: a repeated request returns the first card instead of making another one.
 */
export function useNoteToCard() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      noteId,
      columnId,
      requestId,
    }: {
      noteId: string;
      columnId: string;
      requestId: string;
    }) =>
      api.post<NoteConverted>(`/v1/notes/${noteId}/to-card`, {
        column_id: columnId,
        request_id: requestId,
      }),
    onSuccess: (result) => {
      void client.invalidateQueries({ queryKey: queryKeys.boards.detail(result.board_id) });
      void client.invalidateQueries({ queryKey: queryKeys.calendar.all });
    },
  });
}
